import React from 'react';

// Progress bar component for reuse
const ProgressBar = ({ value, max, colorClass = 'bg-blue-500', bgClass = 'bg-gray-200' }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className={`h-2 rounded-full ${bgClass} overflow-hidden`}>
      <div 
        className={`h-full rounded-full ${colorClass} transition-all duration-500`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

const CountyInfoCard = ({ 
  county, 
  stats, 
  isLoading,
  isProcessing,
  isCompleted 
}) => {
  const { 
    nonEnrichedCount,      // Total records in county (unfiltered)
    enrichedCount,         // Total enriched in county (unfiltered)
    filteredNonEnriched,   // Records in user's acre range (non-enriched)
    filteredEnriched,      // Enriched records in user's acre range
    unverifiedCount,       // Enriched but not ZeroBounce verified
    totalEmailCount,       // Total emails in enriched records
    minAcres,
    maxAcres 
  } = stats || {};

  // Calculate derived values
  // NOTE: filteredNonEnriched is the TOTAL pool (master list), not "awaiting enrichment"
  // filteredEnriched is a SUBSET of filteredNonEnriched (those that have been enriched)
  const totalInRange = filteredNonEnriched || 0;  // Total unique owners in range
  const enrichedInRange = filteredEnriched || 0;  // How many have been enriched
  const enrichmentPercentage = totalInRange > 0 
    ? ((enrichedInRange) / totalInRange * 100).toFixed(1) 
    : 0;
  const remainingToEnrich = Math.max(0, totalInRange - enrichedInRange);
  
  // Email verification calculations
  const pendingVerification = unverifiedCount || 0;
  const totalEnrichedInRange = filteredEnriched || 0;
  const verificationPercentage = totalEnrichedInRange > 0 
    ? ((totalEnrichedInRange - pendingVerification) / totalEnrichedInRange * 100).toFixed(1)
    : 0;

  return (
    <div className={`border rounded-xl shadow-sm transition-all duration-300 ${
      isProcessing ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-white ring-2 ring-blue-300' :
      isCompleted ? 'border-green-400 bg-gradient-to-br from-green-50 to-white' :
      'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
    }`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900 truncate" title={county.county_name}>
            {county.county_name}
          </h4>
          <div className="flex items-center gap-2">
            {isProcessing && (
              <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full animate-pulse flex items-center">
                <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-ping"></span>
                Processing
              </span>
            )}
            {isCompleted && (
              <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Done
              </span>
            )}
          </div>
        </div>
        {/* Acres range badge */}
        <div className="mt-1 flex items-center">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
            📐 {minAcres || 0} - {maxAcres || '∞'} acres
          </span>
        </div>
      </div>
      
      {isLoading ? (
        <div className="p-4 flex items-center justify-center text-gray-500">
          <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full mr-3"></div>
          <span className="text-sm">Loading statistics...</span>
        </div>
      ) : stats ? (
        <div className="p-4 space-y-4">
          {/* Section 1: Enrichment Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                Enrichment Progress
              </span>
              <span className="text-xs font-bold text-purple-600">{enrichmentPercentage}%</span>
            </div>
            
            <ProgressBar 
              value={enrichedInRange} 
              max={totalInRange} 
              colorClass="bg-gradient-to-r from-purple-500 to-indigo-500"
              bgClass="bg-purple-100"
            />
            
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-xs text-gray-500">In Range</div>
                <div className="text-sm font-bold text-gray-800">{totalInRange.toLocaleString()}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-2">
                <div className="text-xs text-purple-600">Enriched</div>
                <div className="text-sm font-bold text-purple-700">{enrichedInRange.toLocaleString()}</div>
              </div>
            </div>
            
            {remainingToEnrich > 0 && (
              <div className="flex items-center text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                <span className="mr-1">📋</span>
                <span>{remainingToEnrich.toLocaleString()} records awaiting enrichment</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Section 2: Email Verification Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Stored Email Inventory
              </span>
              <span className="text-xs font-bold text-green-600">{verificationPercentage}%</span>
            </div>
            
            <ProgressBar 
              value={totalEnrichedInRange - pendingVerification} 
              max={totalEnrichedInRange} 
              colorClass="bg-gradient-to-r from-green-500 to-emerald-500"
              bgClass="bg-green-100"
            />
            
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                <div className="text-xs text-green-600 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified
                </div>
                <div className="text-sm font-bold text-green-700">
                  {(totalEnrichedInRange - pendingVerification).toLocaleString()}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-2 border border-amber-100"
                title="Owners with unverified stored email inventory. This can include suppressed contacts and differs from effective emails pending verification.">
                <div className="text-xs text-amber-600 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Stored email inventory pending
                </div>
                <div className="text-sm font-bold text-amber-700">
                  {pendingVerification.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Email count if available */}
            {totalEmailCount > 0 && (
              <div className="flex items-center justify-between text-xs bg-blue-50 rounded px-2 py-1.5 border border-blue-100">
                <span className="text-blue-600 flex items-center">
                  <span className="mr-1">📧</span>
                  Total Emails Found
                </span>
                <span className="font-semibold text-blue-700">{totalEmailCount.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Quick summary footer */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Total in county:</span>
              <span className="text-gray-600">
                {(nonEnrichedCount || 0).toLocaleString()} records • {(enrichedCount || 0).toLocaleString()} enriched
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 text-center text-sm text-gray-400">
          No data loaded
        </div>
      )}
    </div>
  );
};

export default CountyInfoCard;
